import { useState, useEffect, useRef, useMemo } from 'react';
import {
  ProgressDots,
  ProgressDot,
  DotRating,
  DotShape,
  LegendSection,
  LoadingState,
  ErrorState,
  Chip,
  Button,
  ButtonLink,
  Popover,
} from '@hyggecards/design-system';
import Flashcard from './components/Flashcard';
import Stats from './components/Stats';
import DeckPicker from './components/DeckPicker';
import { AuthButton } from './components/AuthButton';
import { useAuth } from './contexts/AuthContext';
import { useTheme } from '@hyggecards/design-system';
import {
  getNextQuestions,
  getDecks,
  submitRating,
  Flashcard as FlashcardType,
  Stats as StatsType,
  Rating,
  Deck,
  SelectionReason,
  DeckFilters,
} from './services/api';
import { logger } from './services/logger';
import { useSound } from './contexts/SoundContext';
import './App.css';

// Map API rating to design system DotRating
const apiRatingToDotRating = (rating: Rating | 'never' | undefined): DotRating => {
  if (!rating || rating === 'never') return 'unanswered';
  if (rating === '1') return 'hard';
  if (rating === '2') return 'medium';
  if (rating === '3') return 'easy';
  if (rating === 'pass') return 'skip';
  return 'unanswered';
};

// Map selection reason to dot shape
const selectionReasonToShape = (reason?: SelectionReason): DotShape => {
  switch (reason) {
    case 'semantic':
      return 'square';
    case 'new':
      return 'star';
    case 'reinforcement':
      return 'triangle';
    case 'due':
    default:
      return 'circle';
  }
};

// Legend configuration for ProgressDots
const progressDotsLegend: LegendSection[] = [
  {
    title: 'Position',
    items: [
      { label: 'Answered', rating: 'easy', isPast: true },
      { label: 'Current', rating: 'unanswered', isCurrent: true },
      { label: 'Upcoming', rating: 'unanswered' },
    ],
  },
  {
    title: 'Selection Reason',
    items: [
      { label: 'Due for review', rating: 'medium', shape: 'circle', isPast: true },
      { label: 'Related topic', rating: 'medium', shape: 'square', isPast: true },
      { label: 'New card', rating: 'medium', shape: 'star', isPast: true },
      { label: 'Reinforcement', rating: 'medium', shape: 'triangle', isPast: true },
    ],
  },
  {
    title: 'Your Rating',
    items: [
      { label: 'Not rated', rating: 'unanswered' },
      { label: 'Skip', rating: 'skip', isPast: true },
      { label: 'Hard', rating: 'hard', isPast: true },
      { label: 'Medium', rating: 'medium', isPast: true },
      { label: 'Easy', rating: 'easy', isPast: true },
    ],
  },
];

const BATCH_SIZE = 20;
const REFILL_THRESHOLD = 5;
const MANAGEMENT_URL = import.meta.env.VITE_MANAGEMENT_URL;

interface AnsweredCard {
  id: string;
  rating: Rating;
  selectionReason?: SelectionReason;
}

function App() {
  const { user, isLoading: authLoading, isAuthenticated, authEnabled } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { soundEnabled, toggleSound } = useSound();
  const [flashcard, setFlashcard] = useState<FlashcardType | null>(null);
  const [stats, setStats] = useState<StatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [, forceUpdate] = useState({});
  const [personalDecks, setPersonalDecks] = useState<Deck[]>([]);
  const [curatedDecks, setCuratedDecks] = useState<Deck[]>([]);
  const [selectedPersonalDecks, setSelectedPersonalDecks] = useState<string[]>([]);
  const [selectedCuratedDecks, setSelectedCuratedDecks] = useState<string[]>([]);
  const [loadingDecks, setLoadingDecks] = useState(true);
  const flashcardQueueRef = useRef<FlashcardType[]>([]);
  const answeredCardsRef = useRef<AnsweredCard[]>([]);
  const isFetchingRef = useRef(false);
  const fetchPromiseRef = useRef<Promise<void> | null>(null);
  const currentFiltersRef = useRef<DeckFilters>({});
  const lastUserIdRef = useRef<string | null>(null);

  const fetchFlashcardBatch = async (filters?: DeckFilters) => {
    // Use provided filter or current selection
    const filtersToUse = filters ?? currentFiltersRef.current;

    // If already fetching, wait for that fetch to complete
    if (isFetchingRef.current && fetchPromiseRef.current) {
      logger.debug('Already fetching, waiting for it to complete...');
      await fetchPromiseRef.current;
      return;
    }

    logger.debug('Starting fetchFlashcardBatch...');
    isFetchingRef.current = true;

    // Create the fetch promise and store it
    fetchPromiseRef.current = (async () => {
      try {
        const hasFilters =
          (filtersToUse.personalDecks?.length ?? 0) > 0 ||
          (filtersToUse.curatedDecks?.length ?? 0) > 0;
        logger.info(
          `Calling API for ${BATCH_SIZE} flashcards with filters: personal=${filtersToUse.personalDecks?.join(', ') || 'all'}, curated=${filtersToUse.curatedDecks?.join(', ') || 'none'}...`
        );
        const data = await getNextQuestions(BATCH_SIZE, hasFilters ? filtersToUse : undefined);
        logger.debug('API response:', data);
        flashcardQueueRef.current.push(...data.flashcards);
        setStats(data.stats);
        logger.info(
          `Fetched ${data.flashcards.length} flashcards, queue size: ${flashcardQueueRef.current.length}`
        );
      } catch (err) {
        logger.error('fetchFlashcardBatch error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load questions';
        setError(errorMessage);
        throw err;
      } finally {
        isFetchingRef.current = false;
        fetchPromiseRef.current = null;
        logger.debug('fetchFlashcardBatch complete');
      }
    })();

    await fetchPromiseRef.current;
  };

  const loadNextQuestion = async () => {
    logger.debug('loadNextQuestion called');
    setLoading(true);
    setError(null);

    try {
      logger.debug(`Queue size: ${flashcardQueueRef.current.length}`);

      // If queue is low, fetch more in background
      if (flashcardQueueRef.current.length <= REFILL_THRESHOLD && !isFetchingRef.current) {
        logger.debug('Queue low, fetching in background...');
        fetchFlashcardBatch().catch(err => {
          logger.error('Background fetch failed:', err);
        });
      }

      // If queue is empty, wait for fetch
      if (flashcardQueueRef.current.length === 0) {
        logger.debug('Queue empty, waiting for fetch...');
        await fetchFlashcardBatch();
      }

      // Pop next flashcard from queue
      const nextCard = flashcardQueueRef.current.shift();
      logger.debug('Next card:', nextCard);
      if (nextCard) {
        setFlashcard(nextCard);
        logger.debug('Flashcard set successfully');
      } else {
        logger.warn('No flashcard available from queue');
        setError('No flashcards available');
      }
    } catch (err) {
      logger.error('loadNextQuestion error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load question';
      setError(errorMessage);
    } finally {
      setLoading(false);
      logger.debug('loadNextQuestion complete, loading=false');
    }
  };

  useEffect(() => {
    // Wait for auth to finish loading before fetching data
    if (authLoading) {
      return;
    }

    // If auth is enabled but user is not authenticated, don't fetch data yet
    // User needs to sign in first
    if (authEnabled && !isAuthenticated) {
      setLoading(false);
      return;
    }

    // Detect user change (login/logout) and refetch
    const currentUserId = user?.id ?? 'default';
    if (lastUserIdRef.current === currentUserId) {
      // User hasn't changed, skip refetch
      return;
    }

    logger.info(`User changed: ${lastUserIdRef.current} -> ${currentUserId}`);
    lastUserIdRef.current = currentUserId;

    // Clear existing data when user changes
    flashcardQueueRef.current = [];
    answeredCardsRef.current = [];
    setFlashcard(null);
    setSelectedPersonalDecks([]);
    setSelectedCuratedDecks([]);
    currentFiltersRef.current = {};

    // Fetch decks and flashcards for the new user
    const fetchDecksData = async () => {
      try {
        const data = await getDecks();
        setPersonalDecks(data.personalDecks || []);
        setCuratedDecks(data.curatedDecks || []);
        logger.info(
          `Loaded ${data.personalDecks?.length || 0} personal and ${data.curatedDecks?.length || 0} curated decks`
        );
      } catch (err) {
        logger.error('Failed to load decks:', err);
        // Don't set error - decks are optional
      } finally {
        setLoadingDecks(false);
      }
    };

    fetchDecksData();
    loadNextQuestion();
  }, [authLoading, user, authEnabled, isAuthenticated]);

  const handleDeckChange = async (newFilters: DeckFilters) => {
    setSelectedPersonalDecks(newFilters.personalDecks || []);
    setSelectedCuratedDecks(newFilters.curatedDecks || []);
    currentFiltersRef.current = newFilters;

    // Clear the queue and reset state when decks change
    flashcardQueueRef.current = [];
    answeredCardsRef.current = [];
    setFlashcard(null);

    // Load new questions with the new deck filter
    await loadNextQuestion();
  };

  const handleRate = async (rating: Rating) => {
    if (!flashcard) return;

    const flashcardId = flashcard.id;

    // Track answered card with selection reason
    answeredCardsRef.current.push({
      id: flashcardId,
      rating,
      selectionReason: flashcard.selectionReason,
    });

    // Immediately load next question without waiting
    loadNextQuestion();

    // Force re-render to update progress dots
    forceUpdate({});

    // Submit rating asynchronously in the background with context for review history
    submitRating(flashcardId, rating, {
      previousRating: flashcard.rating === 'never' ? undefined : flashcard.rating,
      selectionReason: flashcard.selectionReason,
      deckName: flashcard.deck ?? undefined,
      question: flashcard.question,
    }).catch(err => {
      logger.error('Failed to submit rating:', err);
      // Don't show error to user since they've already moved on
      // The rating will be retried on next interaction if needed
    });
  };

  // Build dots array for design system ProgressDots
  // Combine answered cards + current card + upcoming cards into single array
  const progressDots: ProgressDot[] = useMemo(() => {
    const answered: ProgressDot[] = answeredCardsRef.current.map(card => ({
      id: card.id,
      rating: apiRatingToDotRating(card.rating),
      shape: selectionReasonToShape(card.selectionReason),
    }));

    const current: ProgressDot = flashcard
      ? {
          id: flashcard.id,
          rating: 'unanswered' as DotRating,
          shape: selectionReasonToShape(flashcard.selectionReason),
        }
      : { id: 'placeholder', rating: 'unanswered' as DotRating };

    const upcoming: ProgressDot[] = flashcardQueueRef.current.map(fc => ({
      id: fc.id,
      rating: apiRatingToDotRating(fc.rating),
      shape: selectionReasonToShape(fc.selectionReason),
    }));

    return [...answered, current, ...upcoming];
  }, [flashcard, answeredCardsRef.current.length, flashcardQueueRef.current.length]);

  // Current index is the position of the current card in the dots array
  const currentIndex = answeredCardsRef.current.length;

  return (
    <div className="app">
      <div className="app-header">
        <div className="header-left">
          {isAuthenticated && (
            <>
              <Button
                variant="icon"
                onClick={toggleTheme}
                aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {theme === 'light' ? '🌙' : '☀️'}
              </Button>

              <Button
                variant="icon"
                onClick={toggleSound}
                aria-label={soundEnabled ? 'Turn sound off' : 'Turn sound on'}
              >
                {soundEnabled ? '🔊' : '🔇'}
              </Button>

              <Button
                variant="icon"
                pressed={showInfo}
                onClick={() => setShowInfo(!showInfo)}
                aria-label={showInfo ? 'Hide card info' : 'Show card info'}
              >
                <span className="stc-btn__info-icon">i</span>
              </Button>

              {MANAGEMENT_URL && (
                <ButtonLink
                  variant="icon"
                  href={MANAGEMENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Manage flashcards"
                >
                  &#9881;
                </ButtonLink>
              )}

              {/* Desktop only: Don't show again button */}
              {flashcard && (
                <Button
                  variant="icon"
                  onClick={() => handleRate('hide')}
                  aria-label="Don't show this card again"
                  className="header-hide-btn"
                >
                  ✕
                </Button>
              )}
            </>
          )}
        </div>

        <div className="header-right">{isAuthenticated && <AuthButton />}</div>
      </div>

      <main className="app-main">
        {/* Show loader while auth is loading */}
        {authLoading && <LoadingState message="Loading..." size="large" variant="books" />}

        {/* Show sign-in prompt when auth is enabled but user not authenticated */}
        {authEnabled && !isAuthenticated && !authLoading && (
          <div className="sign-in-prompt">
            <h2>Welcome to HyggeCards</h2>
            <p>Sign in to start learning with your personalized flashcards.</p>
            <div className="sign-in-button-container">
              <AuthButton />
            </div>
          </div>
        )}

        {loading && !flashcard && isAuthenticated && !authLoading && (
          <LoadingState message="Shuffling your cards..." size="large" variant="books" />
        )}

        {error && isAuthenticated && (
          <ErrorState
            title="No flashcards available"
            message={error}
            onRetry={loadNextQuestion}
            retryLabel="Try Again"
          >
            {/* Show helpful prompt if no personal content but curated decks exist */}
            {personalDecks.length === 0 && curatedDecks.length > 0 && (
              <div className="curated-prompt">
                <p>Try our built-in flashcard decks to get started:</p>
                <div className="curated-quick-start">
                  {curatedDecks.slice(0, 3).map(deck => (
                    <Chip
                      key={deck.id}
                      variant="curated"
                      onClick={() =>
                        handleDeckChange({
                          personalDecks: [],
                          curatedDecks: [deck.name.toLowerCase()],
                        })
                      }
                      badge={deck.cardCount}
                    >
                      {deck.name}
                    </Chip>
                  ))}
                </div>
              </div>
            )}
          </ErrorState>
        )}

        {flashcard && !loading && isAuthenticated && (
          <div className="study-session">
            <section className="study-section study-section--progress">
              {/* Desktop: clickable progress dots with popover legend */}
              <div className="progress-dots-desktop">
                <Popover
                  content={
                    <div className="progress-legend-popover">
                      {progressDotsLegend.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="stc-progress-legend__section">
                          <div className="stc-progress-legend__title">{section.title}</div>
                          <div className="stc-progress-legend__items">
                            {section.items.map((item, itemIndex) => (
                              <div key={itemIndex} className="stc-progress-legend__item">
                                <div
                                  className={[
                                    'stc-progress-dot',
                                    `stc-progress-dot--${item.rating ?? 'unanswered'}`,
                                    `stc-progress-dot--shape-${item.shape ?? 'circle'}`,
                                    item.isCurrent && 'stc-progress-dot--current',
                                    item.isPast && 'stc-progress-dot--past',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                />
                                <span>{item.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                  placement="bottom"
                >
                  <div className="progress-dots-trigger">
                    <ProgressDots dots={progressDots} currentIndex={currentIndex} maxVisible={11} />
                  </div>
                </Popover>
              </div>

              {/* Mobile: progress dots with inline legend when showInfo is true */}
              <div className="progress-dots-mobile">
                <ProgressDots
                  dots={progressDots}
                  currentIndex={currentIndex}
                  maxVisible={11}
                  legend={showInfo ? progressDotsLegend : undefined}
                />
              </div>
            </section>

            <section className="study-section study-section--card">
              <Flashcard flashcard={flashcard} onRate={handleRate} showInfo={showInfo} />
            </section>

            <section className="study-section study-section--deck-picker">
              <DeckPicker
                personalDecks={personalDecks}
                curatedDecks={curatedDecks}
                selectedPersonalDecks={selectedPersonalDecks}
                selectedCuratedDecks={selectedCuratedDecks}
                onSelectionChange={handleDeckChange}
                loading={loadingDecks}
              />
            </section>

            {showInfo && stats && (
              <section className="study-section study-section--stats">
                <Stats stats={stats} />
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
